import * as THREE from 'three';
import type { World } from '../../shared/types.js';
import { pointInPoly, resample } from '../../shared/geom.js';
import { Heightfield } from '../../shared/heightfield.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import { hashUnit } from '../../shared/rng.js';

/**
 * Street lighting that lights the street: tarmac, pavement and the bottom two storeys of
 * every wall along it, from hundreds of lights, for one texture read per pixel.
 *
 * Real lights are not an option at this count — three.js is a forward renderer, so every
 * `PointLight` is a loop iteration in every lit fragment shader in the city, and a region
 * has hundreds of lamps. What a street light actually does to a frame, though, is simple
 * enough to bake: it brightens whatever is within ten metres or so of it, and less the
 * higher up you go. So this paints every light into one top-down map of the region — a
 * brightness per square metre or so, plus the ground height there — and the world's
 * materials read it back at their own position and add warm light accordingly.
 *
 * Two kinds of light go into the map:
 *
 * - **The lamp posts** `props.ts` already places on the wide streets, at their heads, so
 *   the brightest light falls exactly under a visible fitting.
 * - **Lanterns nobody can see** on every drivable street too narrow for a post. An old
 *   town's lanes are lit from brackets on the walls; leaving them dark — which is what the
 *   post-only version did — made the Old Town, which is nearly all such lanes, the darkest
 *   place in the game. The light is what reads, from a car; the bracket does not have to.
 *   The same lanterns fill the stretches of a wide street where no post could stand, and
 *   light the named pedestrian streets.
 * - **A grid of standards** over every pedestrian square.
 * - **The lanterns along park paths**, and the lamps OSM has mapped, which `props.ts` places
 *   and hands over with the posts (§93).
 *
 * Nothing about this is per-frame: the map is built once with the world, and `setNight`
 * only changes the strength. At zero it adds exactly nothing, so a daytime frame is what
 * it always was.
 */

export interface StreetLight {
  /**
   * Adds the light to a world material. Chains onto any `onBeforeCompile` already set.
   * `reflect` is for the road alone: it also draws the lamps' reflections in it (§84).
   * `wall` is for the building walls, which take less of the light and lose it sooner with
   * height: a lamp lights a pool on the wall beside it, not the whole of the ground floor.
   * `instanced` is for the props — trees, columns — whose world position goes through the
   * instance matrix as well.
   */
  patch(material: THREE.Material, opts?: { reflect?: boolean; wall?: boolean; instanced?: boolean }): void;
  /** 0 by day, 1 at full night — `WeatherLook.night`. */
  setNight(amount: number): void;
  /** How wet the road is, which is how sharply it reflects the lamps — `WeatherLook.wet`. */
  setWet(amount: number): void;
  dispose(): void;
}

/** A light source to paint into the map: where it is, and how far it reaches. */
export interface StreetLamp {
  x: number;
  z: number;
  /** The lamp's size: 1 for a street lamp, less for a park lantern, which is lower and lights less. */
  scale?: number;
}

/** Sodium-ish, slightly whiter than the pool colour in `props.ts` so a wall does not turn orange. */
const LIGHT_COLOR = new THREE.Color(1, 0.78, 0.52);
/**
 * How much light a fully lit metre of street receives, as a multiple of its own albedo.
 * Tuned by looking at Kraków at midnight: much past this the tarmac under a lamp reads as
 * a lit floor rather than a lit road. It was 1.0 before the tone curve (§80), whose ACES
 * exposure lifts everything dim by about two thirds.
 */
const STRENGTH = 0.8;
/** Added to a surface's albedo before the light is applied — see the shader below. */
const ALBEDO_FLOOR = 0.15;
/** Reach of one light, metres. A real street lamp throws a pool about this size. */
const LAMP_REACH = 13;
const LANTERN_REACH = 12;
/**
 * Brightness of one wall lantern relative to a post. Brighter, not dimmer: a lantern is on
 * the kerb rather than over the road, and has to reach the crown of the street from there.
 */
const LANTERN_POWER = 1.1;
/** Spacing of the invisible lanterns along a narrow street, metres. */
const LANTERN_SPACING = 20;
/**
 * How far from a post a stretch of posted road counts as lit by it. Posts stand every 28 m
 * on alternating kerbs, so every point of a fully posted road is within about this of one.
 */
const GAP_REACH = 22;
/** The grid of lights over a pedestrian square, metres, and the most one square may have. */
const SQUARE_SPACING = 24;
const MAX_SQUARE_LIGHTS = 120;
/**
 * Height band over which the light fades out, metres above the ground under it. A lamp
 * head is at seven metres; a wall is lit to about the second floor and then not at all —
 * which is also what keeps a lit street looking like night rather than like dusk.
 */
const FADE_FROM = 2;
const FADE_TO = 9;
/**
 * On walls: half the light, gone by six metres. At the full share and nine metres, the
 * lanterns along an old-town lane lit both walls end to end to the second floor, and the lane
 * read as floodlit rather than lamplit — exactly the evenness that makes a night street look
 * like a model of one (§84).
 */
const WALL_FADE_TO = 6;
const WALL_SHARE = 0.5;
/**
 * The lamps' heads, for their reflections in the road: how big a head is in the map, and how
 * high it hangs. A post's head is at the top of a 7.4 m pole; a lantern is on a bracket at
 * about first-floor height.
 */
const HEAD_RADIUS = 1.6;
const POST_HEAD_HEIGHT = 7.1;
const LANTERN_HEAD_HEIGHT = 4.6;
/** How bright a reflected head is, before the Fresnel and the wet take their share. */
const REFLECTION = 9;

/** Metres per texel, at best — coarser for a big region so the map stays one 1024² texture. */
const MIN_CELL = 1;
const MAX_TEXELS = 1024;

export function buildStreetLight(
  world: World,
  lamps: StreetLamp[],
  /** Roads at least this wide have lamp posts of their own, and so get no lanterns. */
  postedWidth: number,
): StreetLight {
  const terrain = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
  const margin = LAMP_REACH + 2;
  const minX = -world.extent.x - margin;
  const minZ = -world.extent.z - margin;
  const spanX = (world.extent.x + margin) * 2;
  const spanZ = (world.extent.z + margin) * 2;
  const cell = Math.max(MIN_CELL, Math.max(spanX, spanZ) / MAX_TEXELS);
  const width = Math.ceil(spanX / cell);
  const depth = Math.ceil(spanZ / cell);
  const light = new Float32Array(width * depth);
  const heads = new Float32Array(width * depth);
  const headHeights = new Float32Array(width * depth);

  /** A lamp's head: small, sharp, and tagged with how high it is, for the reflection. */
  const head = (x: number, z: number, height: number, power: number) => {
    const cx = (x - minX) / cell;
    const cz = (z - minZ) / cell;
    const r = Math.max(HEAD_RADIUS / cell, 1.2);
    for (let j = Math.max(0, Math.floor(cz - r)); j <= Math.min(depth - 1, Math.ceil(cz + r)); j++) {
      for (let i = Math.max(0, Math.floor(cx - r)); i <= Math.min(width - 1, Math.ceil(cx + r)); i++) {
        const d2 = ((i + 0.5 - cx) ** 2 + (j + 0.5 - cz) ** 2) / (r * r);
        if (d2 >= 1) continue;
        const k = j * width + i;
        const v = power * (1 - d2) * (1 - d2);
        if (v > heads[k]) {
          heads[k] = v;
          headHeights[k] = height;
        }
      }
    }
  };

  const splat = (x: number, z: number, reach: number, power: number) => {
    const cx = (x - minX) / cell;
    const cz = (z - minZ) / cell;
    const r = reach / cell;
    const x0 = Math.max(0, Math.floor(cx - r));
    const x1 = Math.min(width - 1, Math.ceil(cx + r));
    const z0 = Math.max(0, Math.floor(cz - r));
    const z1 = Math.min(depth - 1, Math.ceil(cz + r));
    for (let j = z0; j <= z1; j++) {
      for (let i = x0; i <= x1; i++) {
        const dx = (i + 0.5 - cx) / r;
        const dz = (j + 0.5 - cz) / r;
        const d2 = dx * dx + dz * dz;
        if (d2 >= 1) continue;
        // Smooth to zero at the rim, so neighbouring pools overlap without a seam — and
        // peaked, cubed rather than squared, so a street lit by a row of them is a row of
        // pools with darker tarmac between, not one even wash (§84).
        const f = 1 - d2;
        light[j * width + i] += power * f * f * f;
      }
    }
  };

  for (const lamp of lamps) {
    const scale = lamp.scale ?? 1;
    // A lantern half the height lights a smaller pool, about as brightly: it is nearer the path.
    splat(lamp.x, lamp.z, LAMP_REACH * (0.45 + 0.55 * scale), 1);
    head(lamp.x, lamp.z, POST_HEAD_HEIGHT * scale, scale < 1 ? 0.8 : 1);
  }

  // Where each post is, bucketed, for finding the stretches of a posted road that have none.
  // A park lantern beside a street is not that street's light.
  const posts = new Map<string, StreetLamp[]>();
  const bucketOf = (x: number, z: number) => `${Math.floor(x / GAP_REACH)},${Math.floor(z / GAP_REACH)}`;
  for (const lamp of lamps) {
    if ((lamp.scale ?? 1) < 1) continue;
    const key = bucketOf(lamp.x, lamp.z);
    const list = posts.get(key);
    if (list) list.push(lamp);
    else posts.set(key, [lamp]);
  }
  const postNear = (x: number, z: number) => {
    const bx = Math.floor(x / GAP_REACH);
    const bz = Math.floor(z / GAP_REACH);
    for (let j = bz - 1; j <= bz + 1; j++) {
      for (let i = bx - 1; i <= bx + 1; i++) {
        for (const lamp of posts.get(`${i},${j}`) ?? []) {
          if ((lamp.x - x) ** 2 + (lamp.z - z) ** 2 < GAP_REACH * GAP_REACH) return true;
        }
      }
    }
    return false;
  };

  for (const road of world.roads) {
    // A footway with a name is a pedestrian street — Aptekarska off Leszno's market, Floriańska
    // in Kraków — and is lit like one. An unnamed one is a path: through a park it is lit by
    // the park's own lanterns, which `props.ts` places, and anywhere else it is not lit.
    if (!ROAD_SPECS[road.cls].drivable && !road.name) continue;
    const posted = road.width >= postedWidth;
    const line = resample(road.pts, LANTERN_SPACING);
    // Alternate sides, starting on a side chosen per road, as the posts do.
    let side = hashUnit(world.seed, road.id ^ 0x5bd1e995) < 0.5 ? 1 : -1;
    for (let i = 0; i < line.length / 2; i++) {
      // A wide road is lit by its posts — except where a post could not stand: a wall at the
      // kerb, the other carriageway of a dual road, the clearance kept round every junction.
      // Each of those used to be a gap of dark road, and a market street lined with shops was
      // nothing but gaps: a fifth of Leszno's first route had no light within 14 m. Those
      // stretches get a lantern on the wall instead, the way the narrow lanes are lit.
      if (posted && postNear(line[i * 2], line[i * 2 + 1])) continue;
      const a = Math.max(0, i - 1);
      const b = Math.min(line.length / 2 - 1, i + 1);
      const dx = line[b * 2] - line[a * 2];
      const dz = line[b * 2 + 1] - line[a * 2 + 1];
      const len = Math.hypot(dx, dz);
      if (len < 1e-3) continue;
      const offset = (road.width / 2 + 1) * side;
      side = -side;
      const lx = line[i * 2] + (-dz / len) * offset;
      const lz = line[i * 2 + 1] + (dx / len) * offset;
      splat(lx, lz, LANTERN_REACH, LANTERN_POWER);
      head(lx, lz, LANTERN_HEAD_HEIGHT, 0.75);
    }
  }

  // Squares. A pedestrian square is an area, not a road, so neither of the passes above ever
  // touched one, and a market square — the brightest place in a real town after dark — was
  // the one black hole in the middle of it. Lit from a loose grid of standards, offset a
  // little per square so two squares side by side are not the same pattern.
  for (const area of world.areas) {
    if (area.kind !== 'paved') continue;
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < area.poly.length; i += 2) {
      x0 = Math.min(x0, area.poly[i]);
      x1 = Math.max(x1, area.poly[i]);
      z0 = Math.min(z0, area.poly[i + 1]);
      z1 = Math.max(z1, area.poly[i + 1]);
    }
    const shift = hashUnit(world.seed, area.id) * SQUARE_SPACING;
    let placed = 0;
    for (let z = z0 + shift / 2; z <= z1 && placed < MAX_SQUARE_LIGHTS; z += SQUARE_SPACING) {
      for (let x = x0 + shift; x <= x1 && placed < MAX_SQUARE_LIGHTS; x += SQUARE_SPACING) {
        if (!pointInPoly(area.poly, x, z)) continue;
        splat(x, z, LAMP_REACH, 1);
        head(x, z, POST_HEAD_HEIGHT * 0.65, 0.75);
        placed++;
      }
    }
  }

  // Overlapping pools add up, and a crossroads of four of them would blow out. `1 - e^-x`
  // is linear for one pool and saturates gently for several.
  const data = new Uint16Array(width * depth * 4);
  for (let j = 0; j < depth; j++) {
    for (let i = 0; i < width; i++) {
      const k = j * width + i;
      data[k * 4] = THREE.DataUtils.toHalfFloat(1 - Math.exp(-light[k] * 1.3));
      // The ground under this texel, so the shader can tell a wall's second floor from its
      // foot on a hillside as well as on the flat.
      const ground = terrain.isFlat ? 0 : terrain.heightAt(minX + (i + 0.5) * cell, minZ + (j + 0.5) * cell);
      data[k * 4 + 1] = THREE.DataUtils.toHalfFloat(ground);
      data[k * 4 + 2] = THREE.DataUtils.toHalfFloat(heads[k]);
      data[k * 4 + 3] = THREE.DataUtils.toHalfFloat(headHeights[k]);
    }
  }

  const texture = new THREE.DataTexture(data, width, depth, THREE.RGBAFormat, THREE.HalfFloatType);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  // One set of uniform objects shared by every patched material, so `setNight` is one write.
  const uniforms = {
    streetField: { value: texture },
    streetRect: { value: new THREE.Vector4(minX, minZ, 1 / (width * cell), 1 / (depth * cell)) },
    streetColor: { value: new THREE.Color(0, 0, 0) },
    streetWet: { value: 0 },
  };

  return {
    patch(material, opts = {}) {
      const previous = material.onBeforeCompile.bind(material);
      const previousKey = material.customProgramCacheKey.bind(material);
      const reflect = opts.reflect === true;
      const wall = opts.wall === true;
      const instanced = opts.instanced === true;
      const fadeTo = wall ? WALL_FADE_TO : FADE_TO;
      const strength = wall ? WALL_SHARE : 1;
      material.onBeforeCompile = (shader, renderer) => {
        previous(shader, renderer);
        Object.assign(shader.uniforms, uniforms);
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vStreetPos;')
          .replace(
            '#include <project_vertex>',
            instanced
              ? '#include <project_vertex>\n\tvStreetPos = ( modelMatrix * instanceMatrix * vec4( transformed, 1.0 ) ).xyz;'
              : '#include <project_vertex>\n\tvStreetPos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;',
          );
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            '#include <common>\n' +
              'uniform sampler2D streetField;\n' +
              'uniform vec4 streetRect;\n' +
              'uniform vec3 streetColor;\n' +
              'uniform float streetWet;\n' +
              'varying vec3 vStreetPos;',
          )
          // After the lights and before the sum, as bounce light: it scales with the
          // surface's own colour, so a red wall goes warm red rather than a flat orange wash.
          //
          // Plus a floor, because tarmac is nearly black — about 8% — and 8% of a street
          // lamp is nothing anybody can see. Under sodium light a dark road reads as a warm
          // grey, not as a darker black, and `ALBEDO_FLOOR` is that.
          .replace(
            '#include <aomap_fragment>',
            '#include <aomap_fragment>\n' +
              '\tvec2 streetSample = texture2D( streetField, ( vStreetPos.xz - streetRect.xy ) * streetRect.zw ).rg;\n' +
              `\tfloat streetReach = 1.0 - smoothstep( ${FADE_FROM.toFixed(1)}, ${fadeTo.toFixed(1)}, vStreetPos.y - streetSample.g );\n` +
              `\treflectedLight.indirectDiffuse += ( diffuseColor.rgb + ${ALBEDO_FLOOR.toFixed(2)} ) * streetColor * ( streetSample.r * streetReach * ${strength.toFixed(2)} );` +
              (reflect ? REFLECTION_GLSL : ''),
          );
      };
      material.customProgramCacheKey = () =>
        `${previousKey()}|streetLight${reflect ? ':reflect' : ''}${wall ? ':wall' : ''}${instanced ? ':instanced' : ''}`;
      material.needsUpdate = true;
    },
    setNight(amount) {
      const night = amount < 0 ? 0 : amount > 1 ? 1 : amount;
      uniforms.streetColor.value.copy(LIGHT_COLOR).multiplyScalar(night * STRENGTH);
    },
    setWet(amount) {
      uniforms.streetWet.value = amount < 0 ? 0 : amount > 1 ? 1 : amount;
    },
    dispose() {
      texture.dispose();
    },
  };
}

/**
 * The lamps in the road: what a wet street at night is mostly made of, and what Slop New
 * York gets from screen-space reflections, which WebGL would make us pay for with a second
 * pass over the whole frame. DECISIONS.md §84.
 *
 * The road is nearly flat, so the reflected ray is the view ray with its height flipped. It
 * is followed up to where a lamp's head would hang — a lantern's 4.6 m, a post's 7.1 — and
 * the map of heads is read there. A head found near the height it was looked for at is a lamp
 * the ray sees. Twelve heights from 3 m to 8.5, the strongest taken, because a rough wet road
 * smears a reflection along the line to the eye and a close run of heights draws that smear as
 * one streak. Four heights, summed, drew each head four times as rings; six drew a ladder of
 * dashes, because at a grazing angle half a metre of height is four metres of road — and
 * twelve still did, further down the street and on a viaduct deck, wet or dry. So the heights
 * are offset per pixel by interleaved gradient noise, a fraction of one step: neighbouring
 * pixels look between each other's rungs, and the ladder fills in to the streak. Weighted
 * by the Fresnel term alone, so it is the grazing reflections down the street that show — the
 * road at your feet, seen from above, reflects almost nothing, which is also true. Only after
 * dark: by day `streetColor` is black and the loop is skipped, for the whole draw at once.
 */
const REFLECTION_GLSL = /* glsl */ `
	if ( streetColor.r > 0.0 ) {
		vec3 streetView = normalize( vStreetPos - cameraPosition );
		vec3 streetRay = vec3( streetView.x, -streetView.y, streetView.z );
		float streetGlint = 0.0;
		float streetJitter = fract( 52.9829189 * fract( dot( gl_FragCoord.xy, vec2( 0.06711056, 0.00583715 ) ) ) );
		for ( int k = 0; k < 12; k ++ ) {
			float lampAt = 3.0 + ( float( k ) + streetJitter ) * 0.5;
			vec2 seen = vStreetPos.xz + streetRay.xz * ( lampAt / max( streetRay.y, 0.04 ) );
			vec4 headAt = texture2D( streetField, ( seen - streetRect.xy ) * streetRect.zw );
			streetGlint = max( streetGlint, headAt.b * exp( -0.5 * pow( ( headAt.a - lampAt ) / 1.1, 2.0 ) ) );
		}
		float streetSheen = mix( 0.06, 1.0, streetWet * ( 1.0 - roughnessFactor ) );
		float streetFresnel = pow( 1.0 - clamp( -streetView.y, 0.0, 1.0 ), 5.0 );
		totalEmissiveRadiance += streetColor * ${REFLECTION.toFixed(1)} * streetGlint * streetSheen * streetFresnel;
	}
`;
